import {
  ProductV2,
  EligibilityStatus,
  CountryConfiguration,
  PurchaseDraftWithProducts,
  PURCHASE_DRAFT_MAX_SKU_COUNT,
  MAX_ROW_SKU_COUNT,
  SupportedLanguage,
  PaymentGateway,
  PURCHASE_STATUS,
  GameId,
} from '@common'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import router from 'next/router'

import { api } from '@/api'
import { HydrateAction, hydrateMatcher } from '@/hydrate'
import { AppThunk, RootState } from '@/store'

import { gtmAddToCart, gtmPurchaseComplete, gtmRemoveFromCart } from '../../utils/gtm'
import { snowplowAddToCart, snowplowCompletePurchase, snowplowRemoveFromCart } from '../../utils/snowplow'
import { resetUser } from '../user/userSlice'

export interface CheckoutRow {
  product: ProductV2
  quantity: number
  eligibility?: EligibilityStatus
}

export interface CheckoutState {
  draft: {
    id: string
    revision: number
    updatedAt: number
    gameId: GameId
  } | null
  acceptedTermsAndConditions: boolean
  billingCountry: CountryConfiguration | null
  creatorBoostCode: {
    value: string
    isValid: boolean
    validating: boolean
  }
  error: {
    title: string
    description: string
    extraDescription?: string
  } | null
  loadingPayment: boolean
  isUpdatingPaymentProvider: boolean
  rows: CheckoutRow[]
  meta: {
    item_list_id: string
    item_list_name: string
  } | null
  paymentProvider: PaymentGateway | null
  locked: boolean
}

export const initialState: CheckoutState = {
  acceptedTermsAndConditions: false,
  billingCountry: null,
  creatorBoostCode: {
    value: '',
    isValid: true,
    validating: false,
  },
  error: null,
  loadingPayment: false,
  isUpdatingPaymentProvider: false,
  rows: [],
  meta: null,
  draft: null,
  paymentProvider: null,
  locked: false,
}

export const completePurchase =
  (purchaseId: string): AppThunk =>
  async (dispatch, getState) => {
    const checkoutMeta = getState().checkout.meta
    const { data: purchase } = await dispatch(api.endpoints.fetchPurchase.initiate(purchaseId))
    if (!purchase) {
      return
    }
    gtmPurchaseComplete(purchase, checkoutMeta)
    const market = getState().config.market
    if (purchase.status === PURCHASE_STATUS.AUTHORISED_WEBHOOK && market) {
      snowplowCompletePurchase(
        purchaseId,
        purchase.paymentMethodName ?? 'N/A',
        purchase.rows,
        router.locale as SupportedLanguage,
        market.currencyCountry,
        market.currencyCode,
      )
    }

    dispatch(clearCheckout())
  }

export const addProductThunk =
  (product: ProductV2): AppThunk<boolean> =>
  (dispatch, getState) => {
    if (
      getState().checkout.rows.reduce((acc, { quantity }) => acc + quantity, 0) >= PURCHASE_DRAFT_MAX_SKU_COUNT ||
      getState().checkout.rows.find((row) => row.product.id === product.id)?.quantity === MAX_ROW_SKU_COUNT
    ) {
      return false
    }
    dispatch(checkoutSlice.actions.addProduct(product))
    gtmAddToCart(product)
    const market = getState().config.market
    if (market) {
      snowplowAddToCart(
        getState().checkout.rows,
        router.locale as SupportedLanguage,
        market.currencyCountry,
        market.currencyCode,
      )
    }
    return true
  }

export const removeProductThunk =
  (product: ProductV2): AppThunk =>
  (dispatch, getState) => {
    dispatch(removeProduct(product))
    gtmRemoveFromCart(product)
    const market = getState().config.market
    if (market) {
      snowplowRemoveFromCart(
        getState().checkout.rows,
        router.locale as SupportedLanguage,
        market.currencyCountry,
        market.currencyCode,
      )
    }
  }

export const clearCheckout = (): AppThunk => async (dispatch) => {
  dispatch(resetCheckoutState())
  dispatch(api.util.invalidateTags(['Draft', 'Session']))
}

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    reCreateCart: (state: CheckoutState) => {
      state.draft = null
    },
    addProduct: (state: CheckoutState, action: PayloadAction<ProductV2>) => {
      const product = action.payload
      const idx = state.rows.findIndex((row) => row.product.id === product.id)

      if (idx > -1) {
        state.rows[idx].quantity = Math.min(state.rows[idx].quantity + 1, 10)
      } else {
        state.rows.push({ product, quantity: 1 })
      }
    },
    removeProduct: (state: CheckoutState, action: PayloadAction<ProductV2>) => {
      const idx = state.rows.findIndex((r) => r.product.id === action.payload.id)
      if (idx === -1) {
        return state
      }
      state.rows[idx].quantity--
      if (state.rows[idx].quantity < 1) {
        state.rows.splice(idx, 1)
      }
    },
    resetCheckoutState: (state) => {
      return {
        ...initialState,
        billingCountry: state.billingCountry,
        creatorBoostCode: state.creatorBoostCode,
      }
    },
    toggleTermsAndConditions: (state) => {
      state.acceptedTermsAndConditions = !state.acceptedTermsAndConditions
    },
    setBillingCountry: (state: CheckoutState, action: PayloadAction<CheckoutState['billingCountry'] | null>) => {
      state.billingCountry = action.payload
    },
    setCreatorBoostCode: (state: CheckoutState, action: PayloadAction<CheckoutState['creatorBoostCode']>) => {
      state.creatorBoostCode = action.payload
    },
    setError: (state: CheckoutState, action: PayloadAction<CheckoutState['error']>) => {
      state.error = action.payload
    },
    setCheckoutMeta: (state: CheckoutState, action: PayloadAction<CheckoutState['meta']>) => {
      state.meta = action.payload
    },
    syncWithDraft: (
      state: CheckoutState,
      action: PayloadAction<{ draft: PurchaseDraftWithProducts | null; market: CountryConfiguration | null }>,
    ) => {
      if (!action.payload.draft) {
        return
      }
      if (action.payload.market && state.billingCountry?.countryCode !== action.payload.market.countryCode) {
        state.billingCountry = action.payload.market
      }
      if (action.payload.draft.creatorBoost) {
        state.creatorBoostCode = {
          value: action.payload.draft.creatorBoost,
          isValid: true,
          validating: false,
        }
      } else {
        state.creatorBoostCode = {
          value: '',
          isValid: true,
          validating: false,
        }
      }
      state.rows = action.payload.draft.rows
      if (action.payload.draft.gameId) {
        state.draft = {
          id: action.payload.draft.id,
          revision: action.payload.draft.revision,
          updatedAt: action.payload.draft.updatedAt,
          gameId: action.payload.draft.gameId,
        }
      }

      state.paymentProvider = action.payload.draft.paymentGateway
    },
    changeLanguage: () => undefined,
    lockCart: (state: CheckoutState, action: PayloadAction<CheckoutState['locked']>) => {
      state.locked = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher<HydrateAction<Partial<RootState>>>(hydrateMatcher, (state, action) => {
        const checkoutState = action.payload?.checkout
        if (checkoutState) {
          state = checkoutState
        }
      })
      .addMatcher(api.endpoints.getDraft.matchFulfilled, (state, action) => {
        if (action.payload && action.payload.gameId) {
          state.draft = {
            id: action.payload.id,
            revision: action.payload.revision,
            updatedAt: action.payload.updatedAt,
            gameId: action.payload.gameId,
          }
          state.paymentProvider = action.payload.paymentGateway
          state.rows = action.payload.rows
        }
      })
      .addMatcher(resetUser.match, (state) => {
        state.draft = null
        state.acceptedTermsAndConditions = false
        state.error = null
      })
      .addMatcher(api.endpoints.updateDraft.matchFulfilled, (state, action) => {
        if (action.payload.gameId) {
          state.draft = {
            id: action.payload.id,
            revision: action.payload.revision,
            updatedAt: action.payload.updatedAt,
            gameId: action.payload.gameId,
          }
        }

        state.paymentProvider = action.payload.paymentGateway
        state.isUpdatingPaymentProvider = false
        state.locked = false
      })
      .addMatcher(api.endpoints.checkoutAdyenSession.matchFulfilled, (state, action) => {
        if (action.payload.payload && state.draft) {
          state.draft = {
            ...state.draft,
            id: action.payload.id,
            revision: action.payload.revision,
            updatedAt: state.draft?.updatedAt ?? Date.now(),
          }
        }
      })
  },
})

export const cartLockedSelector = (state: RootState) =>
  state.checkout.locked ||
  state.api.mutations.checkoutAdyenPayment?.status === 'pending' ||
  state.api.mutations.checkoutAdyenPaymentDetails?.status === 'pending' ||
  state.api.queries.checkoutRazerSession?.status === 'pending'

export const {
  addProduct,
  reCreateCart,
  removeProduct,
  setCreatorBoostCode,
  setError,
  toggleTermsAndConditions,
  setCheckoutMeta,
  setBillingCountry,
  syncWithDraft,
  resetCheckoutState,
  changeLanguage,
  lockCart,
} = checkoutSlice.actions

export default checkoutSlice.reducer
